import type BaseObject from "../models/BaseObject";
import type Connection from "../models/Connection";
import { isVirtualConnection, type AnalyserConnection } from "./VirtualConnections";
type Collection<T> = BaseObject & { asArray(): T[] };
type ConnectionOwner = BaseObject & {
  channelType?: string;
  connections?: Collection<Connection>;
  channels?: Collection<BaseObject>;
  buttonChannels?: Collection<BaseObject>;
};

/** Excludes removed objects and channels explicitly marked as ignored. */
export function isAnalysableObject(object: BaseObject | undefined): object is BaseObject {
  return !!object && !object.isDeleted && (object as ConnectionOwner).channelType !== "Ignored";
}

/** Resolves devices to their own channels; rooms and unrelated containers are deliberately not traversed. */
export function connectionOwners(object: BaseObject | undefined): BaseObject[] {
  if (!isAnalysableObject(object)) return [];
  const result = new Set<BaseObject>([object]);
  const owner = object as ConnectionOwner;
  if (["Actor", "Button", "Device"].includes(object.className)) {
    for (const collection of [owner.channels, owner.buttonChannels]) {
      for (const child of collection?.asArray() ?? []) if (isAnalysableObject(child)) result.add(child);
    }
  }
  return [...result];
}

/** Replaces selection evidence with direct connections and those of the device's channels. */
export function collectSelectedConnections(object: BaseObject | undefined): Connection[] {
  // Derived endpoint edges are supplied by the analysis graph, never as stored evidence.
  if (object?.className === "WireEndpoint") return [];
  const result = new Map<string, Connection>();
  for (const owner of connectionOwners(object)) {
    for (const connection of (owner as ConnectionOwner).connections?.asArray() ?? []) {
      if (!connection.isDeleted && isAnalysableObject(connection.fromDevice) && isAnalysableObject(connection.toDevice))
        result.set(connection.id, connection);
    }
  }
  return [...result.values()];
}

/** Watches collection mutations too, so adding the first connection refreshes the selected device. */
export function connectionWatchTargets(
  object: BaseObject | undefined,
  connections: readonly AnalyserConnection[],
): BaseObject[] {
  const targets = new Set<BaseObject>();
  // Keep observing the source if deletion temporarily removes it until undo.
  if (object) targets.add(object);
  for (const owner of connectionOwners(object)) {
    targets.add(owner);
    const source = owner as ConnectionOwner;
    if (source.connections && !Array.isArray(source.connections)) targets.add(source.connections);
    if (source.channels) targets.add(source.channels);
    if (source.buttonChannels) targets.add(source.buttonChannels);
  }
  for (const connection of connections) {
    if (!isVirtualConnection(connection)) targets.add(connection);
    else {
      for (const consumer of connection.consumers ?? []) {
        targets.add(consumer);
        if (consumer.connections) targets.add(consumer.connections);
      }
      for (const model of connection.modelConnections ?? []) targets.add(model);
    }
    if (connection.fromDevice) targets.add(connection.fromDevice);
    if (connection.toDevice) targets.add(connection.toDevice);
  }
  return [...targets];
}
